/**
 * Backoffice API Service
 * API endpoints for backoffice admin management - uses backend /api/backoffice/*
 */

import { ApiClient, buildQuery } from './client';
import type {
  BackofficeMetrics,
  BackofficeRecentActivity,
  BackofficeUser,
  BackofficeCompany,
  BackofficeCompanyDetail,
  BackofficeStory,
  BackgroundImageVersion,
  StoryResource,
  FullStory,
  UserFilters,
  CompanyFilters,
  StoryFilters,
  UserFormData,
  CompanyFormData,
  StoryFormData,
  BackofficeChartDataPoint,
  NotificationsHealth,
} from '@/types/backoffice.types';
import type { Activity } from '@/types/story.types';

/**
 * Get platform-wide metrics for backoffice dashboard
 */
export async function getBackofficeMetrics(): Promise<BackofficeMetrics> {
  return ApiClient.get<BackofficeMetrics>('/backoffice/metrics');
}

/**
 * Get recent activity for dashboard feed
 */
export async function getBackofficeRecentActivity(): Promise<BackofficeRecentActivity[]> {
  return ApiClient.get<BackofficeRecentActivity[]>('/backoffice/recent-activity');
}

/**
 * Get users with filters
 */
export async function getUsers(
  filters?: UserFilters
): Promise<{ users: BackofficeUser[]; total: number }> {
  const query = buildQuery({
    search: filters?.searchQuery,
    role: filters?.role,
  });
  return ApiClient.get<{ users: BackofficeUser[]; total: number }>(`/backoffice/users${query}`);
}

/**
 * Get user by ID
 */
export async function getUserById(userId: string): Promise<BackofficeUser | null> {
  try {
    return await ApiClient.get<BackofficeUser>(`/backoffice/users/${userId}`);
  } catch {
    return null;
  }
}

/**
 * Create or update user
 */
export async function saveUser(
  userId: string | null,
  data: UserFormData
): Promise<BackofficeUser> {
  const password = (data as UserFormData & { password?: string }).password;
  if (userId) {
    const body: Record<string, unknown> = {
      name: data.name,
      email: data.email,
      role: data.role,
      companyId: data.companyId,
      level: data.level,
    };
    if (password?.trim()) body.password = password;
    return ApiClient.patch<BackofficeUser>(`/backoffice/users/${userId}`, body);
  }
  if (!password?.trim()) {
    throw new Error('Password is required for new users');
  }
  return ApiClient.post<BackofficeUser>('/backoffice/users', {
    name: data.name,
    email: data.email,
    password,
    role: data.role,
    companyId: data.companyId,
    level: data.level,
  });
}

/**
 * Delete user
 */
/**
 * Activar / desactivar una cuenta de usuario. Un usuario inactivo no puede
 * iniciar sesión.
 */
export async function setUserActive(userId: string, isActive: boolean): Promise<BackofficeUser> {
  return ApiClient.patch<BackofficeUser>(`/backoffice/users/${userId}`, { isActive });
}

/**
 * Conceder / revocar la capacidad aditiva "también admin de empresa" a un
 * alumno. El backend valida que sea un alumno con empresa.
 */
export async function setUserCompanyAdmin(
  userId: string,
  isCompanyAdmin: boolean
): Promise<BackofficeUser> {
  return ApiClient.patch<BackofficeUser>(`/backoffice/users/${userId}`, { isCompanyAdmin });
}

export async function deleteUser(userId: string): Promise<void> {
  await ApiClient.delete(`/backoffice/users/${userId}`);
}

/**
 * Get companies with filters
 */
export async function getCompanies(
  filters?: CompanyFilters
): Promise<{ companies: BackofficeCompany[]; total: number }> {
  const query = buildQuery({
    search: filters?.searchQuery,
  });
  return ApiClient.get<{ companies: BackofficeCompany[]; total: number }>(`/backoffice/companies${query}`);
}

/**
 * Get company by ID
 */
export async function getCompanyById(companyId: string): Promise<BackofficeCompanyDetail | null> {
  try {
    return await ApiClient.get<BackofficeCompanyDetail>(`/backoffice/companies/${companyId}`);
  } catch {
    return null;
  }
}

/**
 * Create or update company
 */
export async function saveCompany(
  companyId: string | null,
  data: CompanyFormData
): Promise<BackofficeCompanyDetail> {
  if (companyId) {
    return ApiClient.patch<BackofficeCompanyDetail>(`/backoffice/companies/${companyId}`, {
      name: data.name,
    });
  }
  return ApiClient.post<BackofficeCompanyDetail>('/backoffice/companies', {
    name: data.name,
  });
}

/**
 * Delete company
 */
export async function deleteCompany(companyId: string): Promise<void> {
  await ApiClient.delete(`/backoffice/companies/${companyId}`);
}

/**
 * Get stories with filters
 */
export async function getStories(
  filters?: StoryFilters
): Promise<{ stories: BackofficeStory[]; total: number }> {
  const query = buildQuery({
    search: filters?.searchQuery,
    level: filters?.level,
    status: filters?.status,
  });
  return ApiClient.get<{ stories: BackofficeStory[]; total: number }>(`/backoffice/stories${query}`);
}

/**
 * Get story by ID with full details (resources + activities)
 */
export async function getStoryById(storyId: string): Promise<FullStory | null> {
  try {
    return await ApiClient.get<FullStory>(`/backoffice/stories/${storyId}`);
  } catch {
    return null;
  }
}

/**
 * Create or update story
 */
export async function saveStory(
  storyId: string | null,
  data: StoryFormData
): Promise<BackofficeStory> {
  const body = {
    title: data.title,
    description: data.description,
    titleTranslation: data.titleTranslation,
    descriptionTranslation: data.descriptionTranslation,
    level: data.level,
    category: data.category,
    status: data.status,
    estimatedTime: data.estimatedTime,
    xpReward: data.xpReward,
    backgroundImage: data.backgroundImage ?? null,
  };
  if (storyId) {
    return ApiClient.patch<BackofficeStory>(`/backoffice/stories/${storyId}`, body);
  }
  return ApiClient.post<BackofficeStory>('/backoffice/stories', body);
}

/**
 * Save a fully generated story (metadata + resources + activities) in one request.
 * Used by the AI generation flow after admin approval.
 */
export async function saveGeneratedStory(data: {
  title: string;
  description: string;
  titleTranslation?: string;
  descriptionTranslation?: string;
  level: string;
  category: string;
  type: string;
  estimatedTime: number;
  xpReward: number;
  status: string;
  resources: unknown[];
  activities: unknown[];
  learningObjectives?: unknown[];
}): Promise<BackofficeStory> {
  return ApiClient.post<BackofficeStory>('/backoffice/stories', data);
}

/**
 * Estructura de un error de integridad devuelta por el validador del backend.
 * Hace match con `ActivitiesValidationResult.errorsByIndex` del worker.
 */
export interface ActivityIntegrityError {
  index: number;
  activityId?: string;
  type?: string;
  errors: string[];
}

export interface AuditStoryActivitiesResponse {
  activities: Array<Record<string, unknown>>;
  errors: ActivityIntegrityError[];
  changed: boolean;
}

/**
 * Pide al backend una pasada correctiva on-demand sobre un array de
 * actividades. Devuelve las actividades (posiblemente arregladas) y los
 * errores residuales si la pasada no consigue dejarlas limpias.
 */
export async function auditStoryActivities(
  activities: Array<Record<string, unknown>>
): Promise<AuditStoryActivitiesResponse> {
  return ApiClient.post<AuditStoryActivitiesResponse>(
    '/backoffice/audit-story-activities',
    { activities }
  );
}

export interface AuditStoryAudioScriptResponse {
  content: string;
  errors: string[];
  changed: boolean;
}

/**
 * Pide al backend una pasada correctiva on-demand sobre el `content` del
 * Audio Script. Devuelve el contenido (posiblemente reformateado con saltos)
 * y los errores residuales si la pasada no consigue arreglarlo.
 */
export async function auditStoryAudioScript(
  content: string
): Promise<AuditStoryAudioScriptResponse> {
  return ApiClient.post<AuditStoryAudioScriptResponse>(
    '/backoffice/audit-story-audio-script',
    { content }
  );
}

/**
 * Delete story
 */
export async function deleteStory(storyId: string): Promise<void> {
  await ApiClient.delete(`/backoffice/stories/${storyId}`);
}

/**
 * Get push notifications health metrics for the backoffice dashboard.
 * `days` defaults to 30; backend clamps to a max of 90.
 */
export async function getNotificationsHealth(days = 30): Promise<NotificationsHealth> {
  return ApiClient.get<NotificationsHealth>(
    `/backoffice/notifications/health?days=${days}`
  );
}

/**
 * Get chart data for analytics
 */
export async function getChartData(
  metric: 'users' | 'completions' | 'revenue',
  period: '7d' | '30d' | '90d' | '1y'
): Promise<BackofficeChartDataPoint[]> {
  return ApiClient.get<BackofficeChartDataPoint[]>(`/backoffice/chart-data?metric=${metric}&period=${period}`);
}

// ==============================================
// Activity API Functions (Activity Editor)
// ==============================================

/**
 * Get all resources for a story
 */
export async function getResources(storyId: string): Promise<StoryResource[]> {
  const data = await ApiClient.get<StoryResource[]>(`/backoffice/stories/${storyId}/resources`);
  return Array.isArray(data) ? data : [];
}

/**
 * Save a resource (create or update)
 */
export async function saveResource(
  storyId: string,
  resourceData: Partial<StoryResource>
): Promise<StoryResource> {
  if (resourceData.id) {
    return ApiClient.patch<StoryResource>(
      `/backoffice/stories/${storyId}/resources/${resourceData.id}`,
      resourceData
    );
  }
  return ApiClient.post<StoryResource>(
    `/backoffice/stories/${storyId}/resources`,
    resourceData
  );
}

/**
 * Delete a resource
 */
export async function deleteResource(storyId: string, resourceId: string): Promise<void> {
  await ApiClient.delete(`/backoffice/stories/${storyId}/resources/${resourceId}`);
}

/**
 * Regenerate audio for a story (uses scriptText, content or transcript)
 * @param scriptText - Opcional: script con saltos de línea para historias antiguos sin scriptText guardado
 */
export async function regenerateStoryAudio(
  storyId: string,
  scriptText?: string
): Promise<{ success: boolean; audioUrl: string; duration: number }> {
  return ApiClient.post<{ success: boolean; audioUrl: string; duration: number }>(
    `/backoffice/stories/${storyId}/regenerate-audio`,
    scriptText?.trim() ? { scriptText: scriptText.trim() } : undefined
  );
}

/** Resultado del audit individual de una actividad durante la migración. */
export interface StoryMigrationActivityRepair {
  activityId: string;
  activityType: string;
  issuesDetected: string[];
  repaired: boolean;
  /** Número de llamadas a la IA realizadas (1 = primera pasada bastó; 2 = hizo falta retry). */
  attempts: number;
  /** Issues que quedan tras la última pasada — solo si tras 2 pasadas algo sigue flagged. */
  residualIssues?: string[];
  skipReason?: string;
}

/** Informe que devuelve POST /backoffice/stories/:id/migrate. */
export interface StoryMigrationReport {
  storyId: string;
  storyTitle: string;
  durationMs: number;
  auditSummary: {
    totalActivities: number;
    practiceActivities: number;
    needingRepair: number;
    issuesByKind: Record<string, number>;
  };
  activityRepairs: StoryMigrationActivityRepair[];
  audioScript: {
    wasMalformed: boolean;
    reformatted: boolean;
    audioRegenerated: boolean;
    note?: string;
  };
  storyUpdated: boolean;
}

/**
 * Migra una historia al formato actual: arregla feedback de actividades
 * legacy (sin traducción, con referencias a posición, con la respuesta
 * revelada, success_message residual, etc.) y reformatea el audio script
 * si viene como muro de texto. Si el script cambia, regenera el audio en
 * ElevenLabs automáticamente.
 */
export async function migrateLegacyStory(storyId: string): Promise<StoryMigrationReport> {
  return ApiClient.post<StoryMigrationReport>(
    `/backoffice/stories/${storyId}/migrate`,
    {}
  );
}

/** Genera (o regenera) la imagen de fondo del reto vía OpenAI y la sube al VPS. */
export async function generateStoryBackgroundImage(
  storyId: string
): Promise<{
  success: boolean;
  imageUrl: string;
  version: number;
  history: BackgroundImageVersion[];
}> {
  return ApiClient.post<{
    success: boolean;
    imageUrl: string;
    version: number;
    history: BackgroundImageVersion[];
  }>(`/backoffice/stories/${storyId}/generate-background-image`, {});
}

/**
 * Get all activities for a story
 */
export async function getActivities(storyId: string): Promise<Activity[]> {
  const data = await ApiClient.get<Activity[]>(`/backoffice/stories/${storyId}/activities`);
  return Array.isArray(data) ? data : [];
}

/**
 * Save an activity (create or update)
 */
export async function saveActivity(
  storyId: string,
  activityData: Partial<Activity>
): Promise<Activity> {
  if (activityData.id) {
    return ApiClient.patch<Activity>(
      `/backoffice/stories/${storyId}/activities/${activityData.id}`,
      activityData
    );
  }
  return ApiClient.post<Activity>(
    `/backoffice/stories/${storyId}/activities`,
    activityData
  );
}

/**
 * Delete an activity
 */
export async function deleteActivity(storyId: string, activityId: string): Promise<void> {
  await ApiClient.delete(`/backoffice/stories/${storyId}/activities/${activityId}`);
}

/**
 * Generate audio for an audio activity (audio_translation_select, audio_ordering, audio_dictation).
 * Calls ElevenLabs → uploads to VPS → saves audioUrl in D1.
 * Returns the generated audioUrl.
 */
export async function generateActivityAudio(
  storyId: string,
  activityId: string
): Promise<{ audioUrl: string }> {
  return ApiClient.post<{ audioUrl: string }>(
    `/backoffice/stories/${storyId}/activities/${activityId}/generate-audio`,
    {}
  );
}

/**
 * Reorder an activity
 */
export async function reorderActivity(
  storyId: string,
  activityId: string,
  newOrder: number
): Promise<Activity> {
  return ApiClient.patch<Activity>(
    `/backoffice/stories/${storyId}/activities/${activityId}/reorder`,
    { order: newOrder }
  );
}

export interface ImportStudentRowResult {
  email: string;
  name: string;
  status: 'created' | 'skipped' | 'resent' | 'email_failed' | 'invalid';
  reason?: string;
}

export interface ImportStudentsResult {
  created: number;
  resent: number;
  skipped: number;
  emailFailed: number;
  invalid: number;
  results: ImportStudentRowResult[];
}

/**
 * Alta masiva de alumnos en una empresa desde un CSV ya parseado en el cliente.
 * Crea las cuentas que falten y les envía la invitación manual; los correos ya
 * registrados se saltan.
 */
export function importCompanyStudents(
  companyId: string,
  students: Array<{ name: string; email: string }>,
  resendExisting = false
): Promise<ImportStudentsResult> {
  return ApiClient.post<ImportStudentsResult>(`/backoffice/companies/${companyId}/import`, {
    students,
    resendExisting,
  });
}
